import {
  Checkbox,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { itemsClass, LoadItemTypes, UOMList } from '@common/commonLists';
import {
  BasicTable,
  MenuItemType,
  Notification,
  Popover,
  SelectInput,
  ToastType,
  useIndexedPopoverState,
} from '@components';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLHazmatDetails, LTLLoadItem } from '@store/ltl';
import {
  fetchLtlQuoteLoadItems,
  fetchLtlQuoteParametersByLoadId,
  LoadItemBreakdown,
  LtlQuoteLoadItemViewModel,
  orderStore,
  PickUpDropOffItem,
  saveToLTLCatalog,
} from '@store/orders';
import { initialHazmatDetails } from '@store/orders/defaultVals';
import {
  PlusCircleOutlineIcon,
  DeleteIcon,
  PencilIcon,
  ContentSaveIcon,
  ContentCopyIcon,
  SubdirectoryArrowRightIcon,
} from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import {
  createLoadItemBreakdown,
  getBreakdownDeviations,
  hasActiveBreakdowns,
  removeBreakdown,
  rollUpBreakdowns,
  updateBreakdown,
} from '@utils/ltl/loadItemBreakdown';
import { nestLoadItems } from '@utils/ltl/quoteLoadItems';
import { calculateDensity } from '@utils/ltlUtils';
import { handleToast, printError } from '@utils/utils';
import {
  Accessor,
  createEffect,
  createResource,
  createSignal,
  Index,
  JSXElement,
  Show,
} from 'solid-js';

import cls from './classes';
import { HazardousMaterial } from './HazardousMaterial';
import {
  BreakdownValidationErrors,
  StopItemBreakdown,
} from './StopItemBreakdown';

type LoadItemValidationErrors = {
  quantity?: string[] | null;
  description?: string[] | null;
  weight?: string[] | null;
  length?: string[] | null;
  height?: string[] | null;
  width?: string[] | null;
  lengthInch?: string[] | null;
  heightInch?: string[] | null;
  widthInch?: string[] | null;
  nmfc?: string[] | null;
  pieces?: string[] | null;
  loadItemBreakdowns?: (BreakdownValidationErrors | null)[] | null;
};
interface StopsItemsProps {
  arrayName: string;
  mode?: string;
  stopType: string;
  loadItems: PickUpDropOffItem[];
  setFields: (
    arg: string,
    value:
      | string
      | boolean
      | number
      | LTLHazmatDetails
      | PickUpDropOffItem[]
      | LoadItemBreakdown[]
      | null,
  ) => void;
  addNewListItem: () => void;
  copyLoadItem: (index: number) => void;
  options: MenuItemType[];
  stopIds: { id: number; locationName: string }[];
  stopId: number;
  errors: Record<number, LoadItemValidationErrors> | null;
}
const hasHazmatValues = (detail: unknown): boolean => {
  if (!detail || typeof detail !== 'object') return false;
  const d = detail as Partial<Record<string, string | null | undefined>>;
  return [
    d.unNumber,
    d.packingGroup,
    d.hazardClass,
    d.piece,
    d.contactNumber,
    d.contactName,
  ].some((v) => Boolean(v?.trim()));
};

export const StopItems = (props: StopsItemsProps) => {
  const [isSaving, setIsSaving] = createSignal<boolean>(false);
  const hazmatPopover = useIndexedPopoverState(
    (index) => `[data-hazmat-row-index="${index}"]`,
  );
  const [showPiecesColumn, setShowPiecesColumn] = createSignal<boolean>(false);
  const isMixedCommodityOnLoadEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlSupportMixedCommodityPalletOnLoad,
  );
  const showBreakdowns = () =>
    props.mode === 'LTL' && isMixedCommodityOnLoadEnabled();

  // Load items from the quote this load was booked with, used to flag
  // sub-item weight/pieces that drift from what was quoted.
  const [quotedLoadItems] = createResource(
    () =>
      showBreakdowns()
        ? orderStore.order.loads[orderStore.activeTab.index]?.id
        : undefined,
    async (loadId): Promise<LtlQuoteLoadItemViewModel[]> => {
      if (loadId <= 0) return [];
      try {
        const quoteParams = await fetchLtlQuoteParametersByLoadId(loadId);
        if (!quoteParams.id) return [];
        // The quote API may return sub-items as top-level rows linked by
        // parentQuoteLoadItemId. Nest them so the parent count below matches
        // the load's items and the deviations read the right sub-items.
        return nestLoadItems(await fetchLtlQuoteLoadItems(quoteParams.id));
      } catch (error) {
        printError(error);
        return [];
      }
    },
  );

  createEffect(() => {
    const isFtlNumberOfPiecesEnabled = isFeatureFlagEnabled(
      FeatureFlags.FtlNumberOfPieces,
    )();
    setShowPiecesColumn(props.mode === 'LTL' || isFtlNumberOfPiecesEnabled);
  });

  const getColumns = () => {
    const prevCommonColumnsInit = [
      { name: '# of Units', headerClasses: 'w-[5%]' },
      { name: 'Unit Type', headerClasses: 'w-[7%]' },
      {
        name: 'Item Description',
        headerClasses: 'w-[15%]',
      },
    ];

    const piecesColumn = [
      {
        name: '# of Pieces',
        headerClasses: 'w-[8%]',
      },
    ];

    const prevCommonColumns = showPiecesColumn()
      ? [...prevCommonColumnsInit, ...piecesColumn]
      : prevCommonColumnsInit;

    const ltlColumns = [
      ...prevCommonColumns,
      {
        name: 'Weight',
        headerClasses: props.mode === 'LTL' ? 'w-[7%]' : 'w-[15%]',
      },
      { name: 'Dimensions (in)', headerClasses: 'w-[16%]' },
      { name: 'NMFC', headerClasses: 'w-[8%]' },
      { name: 'Class', headerClasses: 'w-[5%]' },
      { name: 'Hazmat', headerClasses: 'w-[5%]' },
      {
        name: (
          <span>
            PCF (lb/ft<sup>3</sup>)
          </span>
        ),
        headerClasses: 'w-[5%]',
      },
      {
        name: props.stopType == 'Pick Up' ? 'Going To' : 'Coming From',
        headerClasses: 'w-[15%]',
      },
      { name: 'Action', headerClasses: 'w-[4%]' },
    ];

    const otherModeColumns = [
      ...prevCommonColumns,
      { name: 'Weight', headerClasses: 'w-[10%]' },
      { name: 'Dimensions (in)', headerClasses: 'w-[30%]' },
      { name: 'Hazmat', headerClasses: 'w-[5%]' },
      {
        name: props.stopType === 'Pick Up' ? 'Going To' : 'Coming From',
        headerClasses: 'w-[15%]',
      },
      { name: 'Action', headerClasses: 'w-[5%]' },
    ];

    if (props.mode === 'LTL') {
      return ltlColumns;
    }
    return otherModeColumns;
  };

  const footerRow = [
    {
      renderCell: () => (
        <PlusCircleOutlineIcon
          color="#0070A2"
          style={{ cursor: 'pointer' }}
          onClick={props.addNewListItem}
        />
      ),
      key: 'ideaTitle',
    },
  ];

  const getKeyByValue = (value: number) => {
    return props.stopIds.find((item) => item.id === value)?.id.toString() ?? '';
  };

  const onDeleteClick = (index: number) => {
    const loadItems = [...props.loadItems];
    if (loadItems[index].operationType !== 'Insert') {
      loadItems[index].operationType = 'Delete';
    } else {
      loadItems.splice(index, 1);
    }
    props.setFields(props.arrayName, loadItems);
  };

  const onSaveClick = async (index: number) => {
    setIsSaving(true);
    const item = props.loadItems[index];
    const customerId = orderStore.order.customerId;

    if (!Boolean(customerId)) {
      setIsSaving(false);
      return;
    }

    try {
      await saveToLTLCatalog(item, customerId ?? 0);
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemChanges = (
    path: string,
    value: string | boolean | number | LTLHazmatDetails | null,
    idx: number,
  ) => {
    props.setFields(path, value ?? '');
    if (props.loadItems[idx].operationType === 'None') {
      props.setFields(`${props.arrayName}.${idx}.operationType`, 'Update');
    }
  };

  // When an item has sub-items, its weight, pieces, class and NMFC come from them.
  const isRolledUp = (item: PickUpDropOffItem) =>
    showBreakdowns() && hasActiveBreakdowns(item.loadItemBreakdowns);

  // Adding a blank sub-item leaves the parent alone; any edit or delete after
  // that derives the parent's weight, pieces, class and NMFC from the sub-items.
  const setBreakdowns = (
    index: number,
    breakdowns: LoadItemBreakdown[],
    options: { rollUp?: boolean } = {},
  ) => {
    const itemPath = `${props.arrayName}.${index}`;
    props.setFields(`${itemPath}.loadItemBreakdowns`, breakdowns);
    const rollup =
      options.rollUp === false
        ? null
        : rollUpBreakdowns(breakdowns, props.loadItems[index].nmfc);
    if (rollup) {
      props.setFields(`${itemPath}.weight`, rollup.weight);
      props.setFields(`${itemPath}.pieces`, rollup.pieces);
      props.setFields(`${itemPath}.class`, rollup.class);
      props.setFields(`${itemPath}.nmfc`, rollup.nmfc);
    }
    if (props.loadItems[index].operationType === 'None') {
      props.setFields(`${itemPath}.operationType`, 'Update');
    }
  };

  const addBreakdown = (index: number) => {
    const breakdowns = props.loadItems[index].loadItemBreakdowns ?? [];
    setBreakdowns(index, [...breakdowns, createLoadItemBreakdown()], {
      rollUp: false,
    });
  };

  const changeBreakdown = (
    index: number,
    subIndex: number,
    patch: Partial<LoadItemBreakdown>,
  ) => {
    setBreakdowns(
      index,
      updateBreakdown(
        props.loadItems[index].loadItemBreakdowns ?? [],
        subIndex,
        patch,
      ),
    );
  };

  const deleteBreakdown = (index: number, subIndex: number) => {
    setBreakdowns(
      index,
      removeBreakdown(
        props.loadItems[index].loadItemBreakdowns ?? [],
        subIndex,
      ),
    );
  };

  // Load items are matched to quoted items by position among active items,
  // the same way the requote check lines them up.
  const breakdownDeviations = (index: number) => {
    const quoted = quotedLoadItems() ?? [];
    const item = props.loadItems[index];
    const activeItemCount = props.loadItems.filter(
      (loadItem) => loadItem.operationType !== 'Delete',
    ).length;
    if (
      quoted.length === 0 ||
      activeItemCount !== quoted.length ||
      item.operationType === 'Delete'
    ) {
      return [];
    }
    const activeIndex = props.loadItems
      .slice(0, index)
      .filter((loadItem) => loadItem.operationType !== 'Delete').length;
    return getBreakdownDeviations(
      item.loadItemBreakdowns,
      quoted.at(activeIndex)?.quoteLoadItemBreakdown,
    );
  };

  const handleHazmatPopoverClose = (
    index: number,
    rowData: () => PickUpDropOffItem,
  ) => {
    const detail = rowData().loadItemHazmatDetail;
    if (!hasHazmatValues(detail)) {
      handleItemChanges(`${props.arrayName}.${index}.hazmat`, false, index);
      const detailPath = `${props.arrayName}.${index}.loadItemHazmatDetail`;
      if (detail?.loadItemId) {
        props.setFields(detailPath, { ...detail, operationType: 'Delete' });
      } else {
        props.setFields(detailPath, null);
      }
    }
    hazmatPopover.close();
  };

  const notificationsErrors = (index: number) => {
    const errors: Partial<LoadItemValidationErrors | null> =
      props.errors && props.errors[index];
    const showNotification: boolean | null =
      errors &&
      Object.keys(errors).some(
        (item) => errors[item as keyof LoadItemValidationErrors] !== null,
      );
    if (!Boolean(showNotification)) return;
    const elements: JSXElement[] = [];
    Object.entries(errors ?? {}).forEach(([key, value]) => {
      // Sub-item errors are rendered under their own rows in StopItemBreakdown.
      if (key === 'loadItemBreakdowns') return;
      if (value !== null) {
        elements.push(<li>{value as string[]}</li>);
      }
    });
    if (elements.length === 0) return;
    return (
      <td colSpan={getColumns().length}>
        <Notification
          type="error"
          text={<ul class="list-disc my-[8px] pl-[24px]">{elements}</ul>}
        />
      </td>
    );
  };

  const dontShowTableRow = (rowData: PickUpDropOffItem) => {
    let dontShow = false;
    if (
      rowData.operationType === 'Delete' ||
      (rowData.pickUpId !== props.stopId && props.stopType === 'Pick Up') ||
      (rowData.dropOffId !== props.stopId && props.stopType === 'Drop Off')
    ) {
      dontShow = true;
    }
    return dontShow;
  };

  const hazmatCheckbox = (
    rowData: Accessor<PickUpDropOffItem>,
    index: number,
    mode?: string,
  ) => {
    const handleHazmatChange = (val: boolean) => {
      handleItemChanges(`${props.arrayName}.${index}.hazmat`, val, index);
      const hazmatDetail = rowData().loadItemHazmatDetail;

      if (val) {
        if (mode === 'ltl' && hazmatDetail?.operationType === 'Delete') {
          handleItemChanges(
            `${props.arrayName}.${index}.loadItemHazmatDetail`,
            { ...hazmatDetail, operationType: 'Update' },
            index,
          );
        }
      } else {
        if (mode !== 'ltl') {
          handleItemChanges(
            `${props.arrayName}.${index}.loadItemHazmatDetail`,
            null,
            index,
          );
        } else if (hazmatDetail) {
          const newOpType =
            hazmatDetail.operationType === 'Insert' ? null : 'Delete';
          handleItemChanges(
            `${props.arrayName}.${index}.loadItemHazmatDetail`,
            newOpType ? { ...hazmatDetail, operationType: newOpType } : null,
            index,
          );
        }
      }

      // Mirror the LTL quoting screen: the checkbox drives the popover.
      // Toggling hazmat on opens the details popover (via a synthetic
      // mousedown on the pencil trigger); toggling off closes it.
      if (mode === 'ltl') {
        if (val) {
          hazmatPopover.open(index);
        } else {
          hazmatPopover.close();
        }
      }
    };

    return (
      <Checkbox
        checked={Boolean(rowData().hazmat)}
        onChange={handleHazmatChange}
        on:mousedown={(e) => e.stopPropagation()}
        dataTestId={`hazmat-checkboxInput-${index}`}
        size="xs"
        color="info"
        class="ml-[-9px] mr-[4px]"
      />
    );
  };

  return (
    <BasicTable
      columns={getColumns()}
      footerRow={footerRow}
      tableContainerClasses="[&_td]:p-1 [&_th]:p-1"
    >
      <Index each={props.loadItems}>
        {/*eslint-disable*/}
        {(rowData, index) => (
          <>
            <TableRow
              style={{
                "vertical-align": "middle",
                display: dontShowTableRow(rowData()) ? "none" : "table-row",
              }}
            >
              <TableCell>
                <TextField
                  type="number"
                  name={`${props.arrayName}.${index}.quantity`}
                  label=""
                  class={cls.itemFieldInput}
                  placeholder=""
                  style={{ width: "100%" }}
                  value={rowData().quantity}
                  onChange={(e: string) => {
                    handleItemChanges(
                      `${props.arrayName}.${index}.quantity`,
                      e,
                      index,
                    );
                  }}
                  error={Boolean(props.errors && props.errors[index]?.quantity)}
                  size="sm"
                  dataTestId="item_quantity"
                />
              </TableCell>
              <TableCell>
                <SelectInput
                  name={`${props.arrayName}.${index}.item`}
                  label=""
                  class="!h-[36px]"
                  menuItems={LoadItemTypes.map((t) => ({ label: t, value: t }))}
                  value={rowData().item ?? ""}
                  onChange={(val) => {
                    handleItemChanges(
                      `${props.arrayName}.${index}.item`,
                      val,
                      index,
                    );
                  }}
                  dataTestId={formatDataTestId(
                    "Stop item unit type",
                    "selectInput",
                  )}
                />
              </TableCell>
              <TableCell>
                <TextField
                  name={`${props.arrayName}.${index}.description`}
                  label=""
                  class={cls.itemFieldInput}
                  size="sm"
                  placeholder=""
                  style={{ width: "100%" }}
                  value={rowData().description ?? ""}
                  onChange={(e: string) => {
                    handleItemChanges(
                      `${props.arrayName}.${index}.description`,
                      e,
                      index,
                    );
                  }}
                  error={Boolean(
                    props.errors && props.errors[index]?.description,
                  )}
                  maxLength={150}
                  dataTestId="item_description"
                />
              </TableCell>
              {showPiecesColumn() ? (
                <TableCell>
                  <TextField
                    type="number"
                    name={`${props.arrayName}.${index}.pieces`}
                    label=""
                    class={cls.itemFieldInput}
                    size="sm"
                    placeholder=""
                    style={{ width: "100%" }}
                    value={rowData().pieces ?? ""}
                    onChange={(e: string) => {
                      handleItemChanges(
                        `${props.arrayName}.${index}.pieces`,
                        e,
                        index,
                      );
                    }}
                    error={Boolean(props.errors && props.errors[index]?.pieces)}
                    maxLength={7}
                    disabled={isRolledUp(rowData())}
                    dataTestId="pieces-textInput"
                  />
                </TableCell>
              ) : null}
              <TableCell>
                <Grid container class="flex" spacing={2}>
                  <Grid item size={props.mode !== "LTL" ? 6 : 12}>
                    <TextField
                      type="number"
                      name={`${props.arrayName}.${index}.weight`}
                      label=""
                      class={cls.itemFieldInput}
                      placeholder=""
                      style={{ width: "100%" }}
                      value={rowData().weight ?? ""}
                      onChange={(e: string) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.weight`,
                          e === "" ? null : e,
                          index,
                        );
                      }}
                      maxLength={6}
                      error={Boolean(
                        props.errors && props.errors[index]?.weight,
                      )}
                      disabled={isRolledUp(rowData())}
                      size="sm"
                      dataTestId="weight-textInput"
                    />
                  </Grid>
                  {props.mode !== "LTL" ? (
                    <Grid item size={6}>
                      <SelectInput
                        label=""
                        class="!h-[36px]"
                        menuItems={UOMList.map((u) => ({ label: u, value: u }))}
                        value={rowData().uom ?? ""}
                        onChange={(val) => {
                          handleItemChanges(
                            `${props.arrayName}.${index}.uom`,
                            val,
                            index,
                          );
                        }}
                        dataTestId={formatDataTestId(
                          "Stop item UOM",
                          "selectInput",
                        )}
                      />
                    </Grid>
                  ) : null}
                </Grid>
              </TableCell>
              <TableCell>
                <Grid class="flex">
                  {props.mode !== "LTL" ? (
                    <TextField
                      name={`${props.arrayName}.${index}.length`}
                      type="number"
                      label=""
                      class={cls.itemFieldInput}
                      placeholder="ft"
                      style={{ width: "100%" }}
                      value={rowData().length ?? ""}
                      onChange={(e: string) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.length`,
                          e,
                          index,
                        );
                      }}
                      error={Boolean(
                        props.errors && props.errors[index]?.length,
                      )}
                      size="sm"
                      dataTestId="length-textInput"
                    />
                  ) : null}
                  <TextField
                    name={`${props.arrayName}.${index}.lengthInch`}
                    type="number"
                    label=""
                    class={cls.itemFieldInput}
                    placeholder={props.mode == "LTL" ? "L" : "in"}
                    style={{
                      width: "100%",
                      ...(props.mode !== "LTL" && { "margin-left": "5px" }),
                    }}
                    value={rowData().lengthInch ?? ""}
                    onChange={(e: string) => {
                      handleItemChanges(
                        `${props.arrayName}.${index}.lengthInch`,
                        e,
                        index,
                      );
                    }}
                    error={Boolean(
                      props.errors && props.errors[index]?.lengthInch,
                    )}
                    size="sm"
                    dataTestId="lengthinch-textInput"
                  />

                  <span class={cls.xIcon}>X</span>
                  {props.mode !== "LTL" ? (
                    <TextField
                      name={`${props.arrayName}.${index}.width`}
                      type="number"
                      label=""
                      class={cls.itemFieldInput}
                      placeholder="ft"
                      style={{ width: "100%" }}
                      value={rowData().width ?? ""}
                      onChange={(e: string) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.width`,
                          e,
                          index,
                        );
                      }}
                      error={Boolean(
                        props.errors && props.errors[index]?.width,
                      )}
                      size="sm"
                      dataTestId="width-textInput"
                    />
                  ) : null}

                  <TextField
                    name={`${props.arrayName}.${index}.widthInch`}
                    type="number"
                    label=""
                    class={cls.itemFieldInput}
                    placeholder={props.mode == "LTL" ? "W" : "in"}
                    style={{
                      width: "100%",
                      ...(props.mode !== "LTL" && { "margin-left": "5px" }),
                    }}
                    value={rowData().widthInch ?? ""}
                    onChange={(e: string) => {
                      handleItemChanges(
                        `${props.arrayName}.${index}.widthInch`,
                        e,
                        index,
                      );
                    }}
                    error={Boolean(
                      props.errors && props.errors[index]?.widthInch,
                    )}
                    size="sm"
                    dataTestId="widthinch-textInput"
                  />
                  <span class={cls.xIcon}>X</span>
                  {props.mode !== "LTL" ? (
                    <TextField
                      name={`${props.arrayName}.${index}.height`}
                      type="number"
                      label=""
                      class={cls.itemFieldInput}
                      placeholder="ft"
                      style={{ width: "100%" }}
                      value={rowData().height ?? ""}
                      onChange={(e: string) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.height`,
                          e,
                          index,
                        );
                      }}
                      error={Boolean(
                        props.errors && props.errors[index]?.height,
                      )}
                      size="sm"
                      dataTestId="height-textInput"
                    />
                  ) : null}

                  <TextField
                    name={`${props.arrayName}.${index}.heightInch`}
                    type="number"
                    label=""
                    class={cls.itemFieldInput}
                    placeholder={props.mode == "LTL" ? "H" : "in"}
                    style={{
                      width: "100%",
                      ...(props.mode !== "LTL" && { "margin-left": "5px" }),
                    }}
                    value={rowData().heightInch ?? ""}
                    onChange={(e: string) => {
                      handleItemChanges(
                        `${props.arrayName}.${index}.heightInch`,
                        e,
                        index,
                      );
                    }}
                    error={Boolean(
                      props.errors && props.errors[index]?.heightInch,
                    )}
                    size="sm"
                    dataTestId="heightinch-textInput"
                  />
                </Grid>
              </TableCell>
              {props.mode === "LTL" ? (
                <>
                  <TableCell>
                    <TextField
                      name={`${props.arrayName}.${index}.nmfc`}
                      label=""
                      class={cls.itemFieldInput}
                      placeholder=""
                      style={{ width: "100%" }}
                      value={rowData().nmfc ?? ""}
                      onChange={(e: string) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.nmfc`,
                          e,
                          index,
                        );
                      }}
                      error={Boolean(props.errors && props.errors[index]?.nmfc)}
                      maxLength={10}
                      disabled={isRolledUp(rowData())}
                      size="sm"
                      dataTestId="nmfc"
                    />
                  </TableCell>
                  <TableCell>
                    <SelectInput
                      name={`${props.arrayName}.${index}.class`}
                      label=""
                      class="!h-[36px]"
                      value={rowData().class?.toString() ?? ""}
                      menuItems={itemsClass.map((c) => ({
                        label: c,
                        value: c,
                      }))}
                      onChange={(val) => {
                        handleItemChanges(
                          `${props.arrayName}.${index}.class`,
                          val,
                          index,
                        );
                      }}
                      disabled={isRolledUp(rowData())}
                      dataTestId={formatDataTestId(
                        "Stop item freight class",
                        "selectInput",
                      )}
                    />
                  </TableCell>
                </>
              ) : null}
              <TableCell>
                {
                  <span class={`${props.mode !== "LTL" ? "block" : "hidden"}`}>
                    {hazmatCheckbox(rowData, index)}
                  </span>
                }
                {
                  <span class={`${props.mode === "LTL" ? "block" : "hidden"}`}>
                    <div
                      class="flex items-center"
                      data-hazmat-row-index={index}
                    >
                      {hazmatCheckbox(rowData, index, "ltl")}
                      <Popover
                        forceClosePopover={!rowData().hazmat}
                        triggerClose={hazmatPopover.triggerClose(index)}
                        onClick={hazmatPopover.onTriggerClick(index)}
                        eleName={
                          <PencilIcon
                            color={Boolean(rowData().hazmat) ? "black" : "gray"}
                          />
                        }
                        preventFormSubmit
                        classes={cls.reminderTxt}
                        sxProps={{
                          "background-color": "white",
                          "border-radius": "0",
                          "text-decoration": "underline !important",
                        }}
                        class="z-[9999]!"
                        onOutsideClose={() =>
                          handleHazmatPopoverClose(index, rowData)
                        }
                      >
                        <HazardousMaterial
                          fromSidebar={false}
                          loadItemHazmatDetail={
                            rowData().loadItemHazmatDetail ??
                            initialHazmatDetails
                          }
                          onSave={(hazmatDetail: LTLHazmatDetails) => {
                            let opType =
                              rowData().loadItemHazmatDetail?.operationType ??
                              "Insert";
                            if (opType === "None") {
                              opType = "Update";
                            }
                            handleItemChanges(
                              `${props.arrayName}.${index}.loadItemHazmatDetail`,
                              {
                                ...hazmatDetail,
                                operationType: ["None", "Update"].includes(
                                  opType,
                                )
                                  ? "Update"
                                  : opType,
                              },
                              index,
                            );
                            hazmatPopover.close();
                          }}
                          onClose={() =>
                            handleHazmatPopoverClose(index, rowData)
                          }
                        />
                      </Popover>
                    </div>
                  </span>
                }
              </TableCell>
              {props.mode === "LTL" ? (
                <TableCell>
                  <span>
                    {calculateDensity(rowData() as unknown as LTLLoadItem)}
                  </span>
                </TableCell>
              ) : null}
              <TableCell>
                <SelectInput
                  label=""
                  class="!h-[36px]"
                  value={
                    getKeyByValue(
                      (props.stopType && props.stopType === "Pick Up"
                        ? rowData().dropOffId
                        : rowData().pickUpId) ?? 0,
                    ) ?? ""
                  }
                  menuItems={props.options.map((opt) => ({
                    label:
                      typeof opt.label === "string"
                        ? opt.label
                        : String(opt.label),
                    value: String(opt.value),
                  }))}
                  onChange={(val) => {
                    handleItemChanges(
                      `${props.arrayName}.${index}.${
                        props.stopType === "Pick Up" ? "dropOffId" : "pickUpId"
                      }`,
                      props.stopIds.find((stop) => stop.id === Number(val))
                        ?.id ?? 0,
                      index,
                    );
                  }}
                  dataTestId={formatDataTestId(
                    "Stop paired location",
                    "selectInput",
                  )}
                />
              </TableCell>
              <TableCell>
                <Grid
                  container
                  gap={0.5}
                  class="flex flex-nowrap! mt-[10px] items-center"
                >
                  {showBreakdowns() ? (
                    <IconButton
                      title="Add Sub-item"
                      dataTestId="add-stop-item-breakdown-icon-button"
                      on:click={(e) => {
                        e.preventDefault();
                        addBreakdown(index);
                      }}
                    >
                      <SubdirectoryArrowRightIcon class="text-[#026EA1]" />
                    </IconButton>
                  ) : null}
                  {props.mode === "LTL" ? (
                    <IconButton
                      disabled={isSaving()}
                      title="Save Product Line Item to Customer Catalog"
                      dataTestId="save-stop-item-icon-button"
                      on:click={async (e) => {
                        e.preventDefault();
                        await onSaveClick(index);
                      }}
                    >
                      <ContentSaveIcon color="#1B4960" />
                    </IconButton>
                  ) : null}
                  <IconButton
                    dataTestId="copy-stop-item-icon-button"
                    on:click={(e) => {
                      e.preventDefault();
                      props.copyLoadItem(index);
                    }}
                    title="Copy Product Line Item"
                  >
                    <ContentCopyIcon color="black" />
                  </IconButton>
                  <IconButton
                    disabled={isSaving()}
                    title="Delete Product Line Item"
                    dataTestId="delete-stop-item-icon-button"
                    on:click={(e) => {
                      e.preventDefault();
                      onDeleteClick(index);
                    }}
                  >
                    <DeleteIcon color="#B00020" />
                  </IconButton>
                </Grid>
              </TableCell>
            </TableRow>
            <Show when={showBreakdowns()}>
              <StopItemBreakdown
                itemIndex={index}
                breakdowns={rowData().loadItemBreakdowns ?? []}
                hidden={dontShowTableRow(rowData())}
                columnsLength={getColumns().length}
                deviations={breakdownDeviations(index)}
                errors={props.errors?.[index]?.loadItemBreakdowns ?? null}
                onChange={(subIndex, patch) =>
                  changeBreakdown(index, subIndex, patch)
                }
                onDelete={(subIndex) => deleteBreakdown(index, subIndex)}
              />
            </Show>
            {<TableRow>{notificationsErrors(index)}</TableRow>}
          </>
        )}
      </Index>
    </BasicTable>
  );
};
